import { useCallback, useMemo } from 'react'

import { NestedDropdown, NestedDropdownItem } from '@smartbear/swagger-portal-component-library'
import { useFetcher, useRouteLoaderData } from 'react-router'

import { useEnvironment } from '~/shared/hooks/useEnvironment.ts'
import { ACTION } from '~/shared/services/actions'
import { Environment } from '~/shared/types/environment'

interface RootData {
  flags: Partial<Record<string, boolean>>;
  token?: string;
}

interface DebugUIProps {
  featureFlags: string[]
  showResetValues?: boolean
}

export const DebugUI = ({ featureFlags, showResetValues = true }: DebugUIProps) => {
  const env = useEnvironment()

  const isProd = env === Environment.PROD
  const isLocal = env === Environment.LOCAL
  const isDev = env === Environment.DEV

  const fetcher = useFetcher()

  const { flags: rootFlags, token } = useRouteLoaderData<RootData>('root') || { flags: {}, token: '' }

  if (isProd) {
    return null
  }

  const flags = featureFlags.map((value) => {
    if (rootFlags[value] === true) {
      return <span className="cursor-pointer px-4 py-1.5" key={value} onClick={() => {
        fetcher?.submit?.({}, { method: 'POST', action: ACTION.TOGGLE_FEATURE_FLAG({ [value]: false }) })
      }}>{value} &#x2713;</span>
    }

    return <span className="cursor-pointer px-4 py-1.5" key={value} onClick={() => {
      fetcher?.submit?.({}, { method: 'POST', action: ACTION.TOGGLE_FEATURE_FLAG({ [value]: true }) })
    }}>{value}</span>

  })

  const menuItems: NestedDropdownItem[] = useMemo(() => {
    const items = [{
      name: 'Enabled Flags',
      options: flags as any,
    }]

    items.push({
      name: 'Tools',
      options: [
        (isLocal || isDev) && 'Storybook',
        'Copy APP Token',
      ],
    })

    if (showResetValues) {
      items.push({
        name: 'Reset Values',
        options: ['Reset Pre Setup', 'Reset Flag Overrides'],
      })
    }

    return items
  }, [rootFlags, showResetValues])

  const handleDebugOption = useCallback((e) => {
    switch (e) {
      case 'Storybook':
        window.location.href = '/storybook'
        break
      case 'Reset Pre Setup':
        window.location.href = '/sp-admin/presetup?force=true'
        break
      case 'Reset Flag Overrides':
        fetcher?.submit?.({}, { method: 'POST', action: ACTION.CLEAR_FLAG_OVERRIDES() })
        location.reload()
        break
      case 'Copy APP Token':
        try {
          navigator.clipboard.writeText(String(token))
          window.alert('App token copied to clipboard')
        } catch {
          // do nothing
        }
        break
      default:
        break
    }
  }, [rootFlags])

  return (
    <div className="mr-4">
      <NestedDropdown
        dropdownButton="Debug"
        menuItems={menuItems}
        onClick={handleDebugOption}
      />
    </div>
  )
}
